/**
 * Maps LDS package linked-flight `leg` objects to the shape consumed by FlightLegBlock
 * (מנוע טיסות) תוך שימור שדות עשירים ל־footer (מטען, מקומות, הערות).
 */

import { formatPackageLegEndpointHe } from "../../../utils/flightEndpointDisplayHe";

function minutesToDurationHm(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return null;
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return `${h}:${m < 10 ? `0${m}` : m}`;
}

function splitDateTime(full) {
  if (!full || typeof full !== "string") return ["", ""];
  const parts = full.trim().split(/\s+/);
  const date = parts[0] || "";
  const time = parts[1] || "";
  return [date, time];
}

/**
 * תצוגת מספר טיסה עם קוד IATA לפני המספר (למשל 6H502, A3457).
 * אם המספר כבר מתחיל בקוד — מחזירים אותו דחוס בלי רווחים כפולים.
 */
export function formatFlightNumberWithAirlineCode(airlineCode, flightNumber) {
  const fn = flightNumber != null ? String(flightNumber).trim() : "";
  if (!fn) return null;
  const ac =
    airlineCode != null && String(airlineCode).trim() !== ""
      ? String(airlineCode).trim().toUpperCase()
      : "";
  const compact = fn.replace(/\s+/g, "").toUpperCase();
  if (!ac) return fn;
  if (compact.startsWith(ac)) return compact;
  return `${ac}${compact}`;
}

export function resolveAirlineCodeFromPackageLeg(leg, airlines) {
  if (!leg) return null;
  const direct =
    leg.airlineCode != null && String(leg.airlineCode).trim() !== ""
      ? String(leg.airlineCode).trim().toUpperCase()
      : null;
  if (direct) return direct;

  const carrierRaw =
    leg.carrierCode ??
    leg.marketingCarrier ??
    leg.marketingAirlineCode ??
    null;
  if (carrierRaw != null && String(carrierRaw).trim() !== "") {
    return String(carrierRaw).trim().toUpperCase();
  }

  const id = leg.airlineId;
  if (id != null && Array.isArray(airlines)) {
    const byId = airlines.find(
      (a) =>
        a.id != null &&
        String(a.id).toLowerCase() === String(id).toLowerCase()
    );
    if (byId?.code) return String(byId.code).trim().toUpperCase();
    const byCode = airlines.find(
      (a) =>
        a.code != null &&
        String(a.code).toLowerCase() === String(id).toLowerCase()
    );
    if (byCode?.code) return String(byCode.code).trim().toUpperCase();
  }

  if (leg.flightNumber && String(leg.flightNumber).length >= 2) {
    return String(leg.flightNumber)
      .slice(0, 2)
      .toUpperCase();
  }
  return null;
}

export function resolveAirlineNameFromPackageLeg(leg, airlines) {
  if (!leg || !Array.isArray(airlines)) return "";
  const id = leg.airlineId;
  if (id == null) return "";
  const byId = airlines.find(
    (a) => a.id != null && String(a.id).toLowerCase() === String(id).toLowerCase()
  );
  if (byId?.name) return String(byId.name);
  const byCode = airlines.find(
    (a) =>
      a.code != null &&
      String(a.code).toLowerCase() === String(id).toLowerCase()
  );
  return byCode?.name ? String(byCode.name) : "";
}

/**
 * @param {object} leg — קטע מ־flight[].leg (חבילות נופש)
 * @param {{ labelHe: string, airlines?: array }} opts
 */
export function packageLegToFlightLegBlockLeg(leg, { labelHe, airlines = [] }) {
  if (!leg) return null;

  const [depDateStr, depTimeFromField] = splitDateTime(leg.depDate);
  const [arrDateStr, arrTimeFromField] = splitDateTime(leg.arrDate);

  const depTime =
    (leg.depTime && String(leg.depTime).trim()) || depTimeFromField || "—";
  const arrTime =
    (leg.arrTime && String(leg.arrTime).trim()) || arrTimeFromField || "—";

  const originCode = leg.depAirport?.code || leg.depCity || "—";
  const destinationCode = leg.arrAirport?.code || leg.arrCity || "—";

  const originCityLabel =
    leg.depCity && String(leg.depCity).trim() !== String(originCode).trim()
      ? String(leg.depCity).trim()
      : "";
  const destinationCityLabel =
    leg.arrCity && String(leg.arrCity).trim() !== String(destinationCode).trim()
      ? String(leg.arrCity).trim()
      : "";

  const secondaryArrDateLabel =
    arrDateStr && depDateStr && arrDateStr !== depDateStr
      ? `נחיתה ${arrDateStr}`
      : null;

  const airlineCode = resolveAirlineCodeFromPackageLeg(leg, airlines);

  const cabin =
    leg.flightClass != null && String(leg.flightClass).trim() !== ""
      ? String(leg.flightClass).trim()
      : "";
  const category =
    leg.flightCategory != null && String(leg.flightCategory).trim() !== ""
      ? String(leg.flightCategory).trim()
      : "";

  const flightCategoryForBlock = cabin || category || undefined;
  const typeBadgeExtra =
    cabin && category && category !== cabin ? category : "";

  const legCount = Number(leg.legCount ?? leg.numLegs);
  let isDirect = true;
  let stopLabelHe = "טיסה ישירה";
  if (leg.isDirect === false) {
    isDirect = false;
    stopLabelHe = leg.stopLabelHe || "עם עצירות";
  } else if (Number.isFinite(legCount) && legCount > 1) {
    isDirect = false;
    stopLabelHe =
      leg.stopLabelHe ||
      (legCount === 2 ? "עצירה אחת" : `${legCount - 1} עצירות`);
  }

  const durationHm = minutesToDurationHm(leg.duration);

  const originLocationLineHe = formatPackageLegEndpointHe(leg, "dep");
  const destinationLocationLineHe = formatPackageLegEndpointHe(leg, "arr");

  return {
    mapped: {
      id: leg.id,
      labelHe: labelHe || "הלוך",
      depDateDisplay: depDateStr || null,
      depDate: depDateStr || undefined,
      arrDate: arrDateStr || undefined,
      departureTime: depTime,
      arrivalTime: arrTime,
      originCode,
      destinationCode,
      originCityLabel: originCityLabel || undefined,
      destinationCityLabel: destinationCityLabel || undefined,
      originLocationLineHe,
      destinationLocationLineHe,
      secondaryArrDateLabel: secondaryArrDateLabel || undefined,
      isDirect,
      stopLabelHe,
      duration: durationHm,
      flightNumber: leg.flightNumber,
      flightCategory: flightCategoryForBlock || undefined,
      airlineCode,
    },
    typeBadgeExtra,
    rawLeg: leg,
  };
}
